import * as React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from '../reducers/reducers';
import { FieldBinaryOutlined, CaretDownOutlined, NodeExpandOutlined, FireOutlined } from '@ant-design/icons';
import CreateEditReleaseModal from './CreateEditReleaseModal';
import { ReleaseStatus, ReleaseTimelinePhase } from '../types';
import { NewReleasePayloadType, tryNewReleaseAction } from '../actions/release';
import { AppComponentProps } from '../typescriptHelpers';
import ReleaseGroup from './ReleaseGroup';
import Dropdown from 'antd/lib/dropdown';
import Text from 'antd/lib/typography/Text';
import Spin from 'antd/lib/spin';
import {
    selectOrg,
    selectApp,
    selectPaginatedReleasesLimitAndOffset,
    selectIsAppsDemoModeEnabled,
    selectIsWorkflowsSSRStepsEnabled,
} from '../selectors/selectors';
import { ReleasesRequest, tryFetchPaginatedEntities } from '../actions/entities';
import { FetchableEntityType, FetchablePaginatedEntityType } from '../entityType';
import PrepareHotfixOrRollbackReleaseModal from './rollbacks/PrepareHotfixOrRollbackReleaseModal';
import { PrepareNewWorkflowRunModal } from './prepareRelease/PrepareNewWorkflowRunModal';
import { selectCompletedReleases, selectCurrentReleases, selectUpcomingReleases } from '@/selectors/memoizedSelectors';
import { getIsLoading } from '../helpers';
import { antGrey, runwayColors, navDropdownMenuStyle } from './styles/globalStyles';

enum PrepareReleaseType {
    Standard = 'release',
    Hotfix = 'hotfix',
}

interface State {
    createEditReleaseModalVisible: boolean;
    cretateEditHotfixModalVisible: boolean;
    isDropdownOpen: boolean;
}

class Releases extends React.Component<Props, State> {
    constructor(props) {
        super(props);

        this.state = {
            createEditReleaseModalVisible: false,
            cretateEditHotfixModalVisible: false,
            isDropdownOpen: false,
        };
    }

    componentDidMount(): void {
        if (this.props.shouldFetchCompletedReleases && !this.props.hasCompletedReleases) {
            this.fetchCompletedReleases();
        }
    }

    componentDidUpdate(prevProps: Props) {
        const shouldFetchBecameTrue =
            !prevProps.shouldFetchCompletedReleases && this.props.shouldFetchCompletedReleases;

        if (shouldFetchBecameTrue && !this.props.hasCompletedReleases) {
            this.fetchCompletedReleases();
        }
    }

    fetchCompletedReleases = () => {
        if (!this.props.shouldFetchCompletedReleases) {
            return;
        }

        const limit = this.props.releasesPaginationLimit ?? 10;
        const offset = this.props.releasePaginationOffset ?? 0;

        this.props.fetchReleases(
            {
                orgId: this.props.orgId,
                appId: this.props.appId,
                orgIdParam: this.props.orgIdParam,
                appIdParam: this.props.appIdParam,
            },
            limit,
            offset,
        );
    };

    handleMenuClick = (e) => {
        if (e.key == PrepareReleaseType.Hotfix) {
            this.setState({
                ...this.state,
                cretateEditHotfixModalVisible: true,
            });
        } else if (e.key === PrepareReleaseType.Standard) {
            this.setState({
                ...this.state,
                createEditReleaseModalVisible: true,
            });
        }
    };

    menuItems = [
        {
            key: PrepareReleaseType.Standard,
            label: (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '4px 0px',
                        borderRadius: 'inherit',
                    }}
                >
                    <NodeExpandOutlined style={{ color: runwayColors.primary, width: 16 }} />
                    <Text style={{ color: antGrey.grey1, fontSize: 14 }}>Release</Text>
                </div>
            ),
        },
        {
            key: PrepareReleaseType.Hotfix,
            label: (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '4px 0px',
                        borderRadius: 'inherit',
                    }}
                >
                    <FireOutlined style={{ color: runwayColors.statusYellow, fontSize: 16 }} />
                    <Text style={{ color: antGrey.grey2, fontSize: 14 }}>Hotfix</Text>
                </div>
            ),
        },
    ];

    releaseGroups = () => {
        const hasNoReleases =
            !this.props.hasUpcomingReleases && !this.props.hasCurrentReleases && !this.props.hasCompletedReleases;

        if (hasNoReleases && this.props.isLoadingReleases) {
            return (
                <div style={{ minHeight: '40vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Spin style={{ fontSize: 15 }} />
                </div>
            );
        }

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {this.props.hasUpcomingReleases && (
                    <ReleaseGroup
                        orgId={this.props.orgId}
                        appId={this.props.appId}
                        groupPhase={ReleaseTimelinePhase.Upcoming}
                        orgIdParam={this.props.orgIdParam}
                        appIdParam={this.props.appIdParam}
                    />
                )}
                {this.props.hasCurrentReleases && (
                    <ReleaseGroup
                        orgId={this.props.orgId}
                        appId={this.props.appId}
                        groupPhase={ReleaseTimelinePhase.Current}
                        orgIdParam={this.props.orgIdParam}
                        appIdParam={this.props.appIdParam}
                    />
                )}
                {this.props.hasCompletedReleases && (
                    <ReleaseGroup
                        orgId={this.props.orgId}
                        appId={this.props.appId}
                        groupPhase={ReleaseTimelinePhase.Completed}
                        orgIdParam={this.props.orgIdParam}
                        appIdParam={this.props.appIdParam}
                        onClickLoadMore={this.fetchCompletedReleases}
                        totalCount={this.props.completedReleaseTotalCount}
                        canLoadMore={this.props.shouldFetchCompletedReleases}
                    />
                )}
                {!this.props.hasCompletedReleases && this.props.isLoadingReleases && (
                    <div style={{ height: 50, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Spin style={{ fontSize: 15 }} />
                    </div>
                )}
            </div>
        );
    };

    renderModals() {
        if (this.props.isSSRMigrated) {
            return (
                <>
                    <PrepareNewWorkflowRunModal
                        orgId={this.props.orgId}
                        appId={this.props.appId}
                        open={this.state.createEditReleaseModalVisible}
                        mode="standard"
                        onCancel={() => {
                            this.setState({
                                ...this.state,
                                createEditReleaseModalVisible: false,
                            });
                        }}
                        onSuccess={() => {
                            this.setState({
                                ...this.state,
                                createEditReleaseModalVisible: false,
                            });
                        }}
                    />
                    <PrepareNewWorkflowRunModal
                        orgId={this.props.orgId}
                        appId={this.props.appId}
                        open={this.state.cretateEditHotfixModalVisible}
                        mode="hotfix"
                        isConvertingRollback={this.props.lastCompletedReleaseHasActiveRollback}
                        onCancel={() => {
                            this.setState({
                                ...this.state,
                                cretateEditHotfixModalVisible: false,
                            });
                        }}
                        onSuccess={() => {
                            this.setState({
                                ...this.state,
                                cretateEditHotfixModalVisible: false,
                            });
                        }}
                    />
                </>
            );
        }

        return (
            <>
                <CreateEditReleaseModal
                    orgId={this.props.orgId}
                    appId={this.props.appId}
                    open={this.state.createEditReleaseModalVisible}
                    onCancel={() => {
                        this.setState({
                            ...this.state,
                            createEditReleaseModalVisible: false,
                        });
                    }}
                    onDidCreateOrEditRelease={() => {
                        this.setState({
                            ...this.state,
                            createEditReleaseModalVisible: false,
                        });
                    }}
                    isEditingRelease={false}
                    displayOnlyTargetDates={false}
                    canBeSubmitted={null}
                />
                <PrepareHotfixOrRollbackReleaseModal
                    orgId={this.props.orgId}
                    appId={this.props.appId}
                    open={this.state.cretateEditHotfixModalVisible}
                    isConvertingRollback={this.props.lastCompletedReleaseHasActiveRollback}
                    onCancel={() => {
                        this.setState({
                            ...this.state,
                            cretateEditHotfixModalVisible: false,
                        });
                    }}
                />
            </>
        );
    }

    render() {
        return (
            <div>
                {this.renderModals()}
                <div style={{ padding: '20px 16px 10px 16px' }}>
                    <Dropdown
                        menu={{
                            items: this.menuItems,
                            onClick: this.handleMenuClick,
                            style: navDropdownMenuStyle,
                            className: 'dark-dropdown-menu',
                        }}
                        trigger={['click']}
                        onOpenChange={(open) => this.setState({ isDropdownOpen: open })}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: 'pointer',
                                padding: '5px 12px',
                                backgroundColor: this.state.isDropdownOpen ? 'black' : 'transparent',
                                border: `1px solid ${antGrey.grey7}`,
                                borderRadius: 4,
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <FieldBinaryOutlined style={{ color: antGrey.grey6, fontSize: 16 }} />
                                <Text style={{ color: antGrey.grey2, fontSize: 14 }}>Prepare new...</Text>
                            </div>
                            <CaretDownOutlined style={{ color: antGrey.grey7, fontSize: 16, marginLeft: 12 }} />
                        </div>
                    </Dropdown>
                </div>
                <div>{this.releaseGroups()}</div>
            </div>
        );
    }
}

type ReleasesProps = AppComponentProps;

const mapProps = (state: RootState, ownProps: ReleasesProps) => {
    const { orgId, appId } = ownProps;

    const org = selectOrg(state, orgId);
    const app = selectApp(state, appId);
    const completedReleases = selectCompletedReleases(state, appId);
    const currentReleases = selectCurrentReleases(state, appId);
    const upcomingReleases = selectUpcomingReleases(state, appId);
    const completedReleaseTotalCount = app?.completedReleaseTotalCount ?? 0;
    const isDemoModeEnabled = selectIsAppsDemoModeEnabled(state);

    const paginatedReleasesPageLimitAndOffset = selectPaginatedReleasesLimitAndOffset(state, appId);

    const releasesPaginationLimit = paginatedReleasesPageLimitAndOffset?.limit;
    const releasePaginationOffset = paginatedReleasesPageLimitAndOffset?.offset;
    const shouldFetchCompletedReleases =
        (releasePaginationOffset ?? 0) < completedReleaseTotalCount && !isDemoModeEnabled;

    const lastCompletedReleaseHasActiveRollback =
        completedReleases.length > 0 ? completedReleases[0].rollbackInfo?.status === ReleaseStatus.Active : false;

    const isSSRMigrated = selectIsWorkflowsSSRStepsEnabled(state, appId);
    const isLoadingAppState = getIsLoading(FetchableEntityType.AppState, state, appId);
    const isLoadingCompletedReleases = getIsLoading(FetchablePaginatedEntityType.Releases, state, appId);
    const isLoadingReleases = isLoadingAppState || isLoadingCompletedReleases;

    return {
        appId,
        orgId,
        orgIdParam: org?.orgId ?? '',
        appIdParam: app?.appId ?? '',
        releasesPaginationLimit,
        releasePaginationOffset,
        shouldFetchCompletedReleases,
        completedReleaseTotalCount,
        lastCompletedReleaseHasActiveRollback,
        isSSRMigrated,
        isLoadingReleases,
        hasUpcomingReleases: upcomingReleases.length > 0,
        hasCurrentReleases: currentReleases.length > 0,
        hasCompletedReleases: completedReleases.length > 0,
    };
};

const mapDispatch = (dispatch) => {
    return {
        createNewRelease: (payload: NewReleasePayloadType) => {
            dispatch(tryNewReleaseAction(payload));
        },
        fetchReleases: (request: ReleasesRequest, limit: number, offset: number) => {
            dispatch(
                tryFetchPaginatedEntities(FetchablePaginatedEntityType.Releases, request, limit, offset, request.appId),
            );
        },
    };
};

const connector = connect(mapProps, mapDispatch);

type Props = ConnectedProps<typeof connector>;

export default connector(Releases);
